import { useScrubberDrag } from '@src/hooks/useScrubberDrag';
import { SAFE_ZONE_STRINGS } from '@src/safe-zones/strings';
import BackIcon from '@src/svgs/BackIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import LikesIcon from '@src/svgs/LikesIcon';
import MusicNoteIcon from '@src/svgs/MusicNoteIcon';
import SearchIcon from '@src/svgs/SearchIcon';
import YouTubeRemixIcon from '@src/svgs/YouTubeRemixIcon';
import YouTubeShareIcon from '@src/svgs/YouTubeShareIcon';
import YouTubeShortsCommentIcon from '@src/svgs/YouTubeShortsCommentIcon';
import React from 'react';
import styled from 'styled-components';

import { BrandLogo, BrandName, StoryTitle } from '../BrandSlots';
import { useChromeFontSize } from '../useChromeFontSize';

import type { SafeZoneProps } from '@src/safe-zones/types';

const COPY = SAFE_ZONE_STRINGS.overlays.youtubeShortFormVideo;

/**
 * YouTube Shorts overlay — paints YouTube's full-screen vertical short-video
 * surface chrome over the clip-preview rect: a top "Shorts" wordmark, a
 * right-side action rail (like / dislike / comments / share / remix /
 * rotating sound disc), a bottom strip with channel avatar + handle +
 * Subscribe pill on row one, video title + "...more" on row two, and a
 * sound-attribution row on row three. A thin always-on red scrubber line
 * lives at the very bottom.
 *
 * 9:16 only. YouTube Shorts is vertical-only on the real surface; non-9:16
 * uploads are rejected at the platform layer.
 *
 * Scaling: chrome is sized in `em` against a base font-size derived from
 * **both** rect dimensions. Reference rect **360×640** → 1em = 10px.
 * Scale factor `min(rect.width / 360, rect.height / 640)`. There is no
 * lower floor — the chrome shrinks linearly with the rect so the entire
 * overlay stays in proportion at every preview size. The upper cap
 * (`SCALE_MAX`) is still in place so absurdly-large previews don't render
 * absurdly-large chrome. `SCALE_FLOOR` is a tiny `0.05` safety floor so a
 * zero-area rect doesn't produce a `0` font-size. Identical scaling shape
 * to LinkedIn Shorts so the two shorts overlays read at parity.
 */
const REFERENCE_RECT_WIDTH = 360;
const REFERENCE_RECT_HEIGHT = 640;
const REFERENCE_FONT_PX = 10;
const SCALE_FLOOR = 0.05;
const SCALE_MAX = 3;

export const YouTubeShortsOverlay: React.FC<SafeZoneProps> = ({
  rect,
  variants,
  playback,
}) => {
  const fontPx = useChromeFontSize(rect, {
    referenceWidth: REFERENCE_RECT_WIDTH,
    referenceHeight: REFERENCE_RECT_HEIGHT,
    referenceFontPx: REFERENCE_FONT_PX,
    scaleMin: SCALE_FLOOR,
    scaleMax: SCALE_MAX,
  });

  const showCaptions = !!variants.showCaptions;

  // Always-on bottom scrubber — same shared drag-and-seek hook LinkedIn
  // Shorts uses, so seek behavior stays in lockstep. YouTube Shorts has no
  // controls bar; the thin red line at the very bottom is the only playback
  // affordance.
  const {
    scrubberRef,
    displayTime,
    progressPct,
    scrubberInteractive,
    onMouseDown: onScrubberMouseDown,
  } = useScrubberDrag(playback);
  const duration = playback?.duration ?? 0;

  return (
    // Frame fills the video rect (9:16). All chrome positions resolve
    // against the Frame's bounds.
    <Frame
      data-testid="youtube-shorts-overlay"
      style={{ fontSize: `${fontPx}px` }}
    >
      <TopStrip>
        <BackButton type="button" aria-label={COPY.aria.back}>
          <BackIcon width="2em" height="1.4em" strokeColor="#ffffff" />
        </BackButton>
        <ShortsWordmark aria-label={COPY.aria.shorts}>
          <ShortsLabel>{COPY.shortsLabel}</ShortsLabel>
        </ShortsWordmark>
        <TopSpacer />
        <TopActionBtn type="button" aria-label={COPY.aria.search}>
          <SearchIcon width="1.8em" height="1.8em" fillColor="#ffffff" />
        </TopActionBtn>
        <TopActionBtn type="button" aria-label={COPY.aria.more}>
          <EllipsisIcon width="1.6em" height="0.4em" fillColor="#ffffff" />
        </TopActionBtn>
      </TopStrip>

      <RightRail>
        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.like}>
            <LikesIcon width="2.6em" height="2.6em" strokeColor="#ffffff" />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.like}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.dislike}>
            {/* Reuse LikesIcon rotated 180° instead of shipping a near-
                duplicate ThumbsDown SVG. YouTube hides the dislike count
                under the thumb on the real rail — instead, the literal
                word "Dislike" paints in the same slot a numeric count
                would occupy under Like / Comment. */}
            <FlippedGlyph>
              <LikesIcon width="2.6em" height="2.6em" strokeColor="#ffffff" />
            </FlippedGlyph>
          </ActionIconBtn>
          <ActionCount>{COPY.counts.dislike}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.comment}>
            <YouTubeShortsCommentIcon
              width="2.6em"
              height="2.6em"
              strokeColor="#ffffff"
            />
          </ActionIconBtn>
          <ActionCount>{COPY.counts.comment}</ActionCount>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.share}>
            <YouTubeShareIcon
              width="2.6em"
              height="2.6em"
              strokeColor="#ffffff"
            />
          </ActionIconBtn>
        </ActionItem>

        <ActionItem>
          <ActionIconBtn type="button" aria-label={COPY.aria.remix}>
            <YouTubeRemixIcon
              width="2.6em"
              height="2.6em"
              fillColor="#ffffff"
            />
          </ActionIconBtn>
        </ActionItem>

        {/* Sound disc — static channel avatar at the bottom of the rail.
            YouTube Shorts does NOT spin the disc on the real surface, so
            the safe-zone preview paints it stationary as well. */}
        <SoundDiscBtn type="button" aria-label={COPY.aria.sound}>
          <SoundDiscRing>
            <SoundDiscInner>
              <BrandLogo />
            </SoundDiscInner>
          </SoundDiscRing>
        </SoundDiscBtn>
      </RightRail>

      {/* Caption — always mounted, fades in/out via opacity */}
      <CaptionAnchor $visible={showCaptions}>
        <CaptionBox>
          <CaptionLines>{COPY.captionPlaceholder}</CaptionLines>
        </CaptionBox>
      </CaptionAnchor>

      <BottomStrip>
        <BrandRow>
          <Avatar>
            <BrandLogo />
          </Avatar>
          <ChannelNameText>
            @<BrandName />
          </ChannelNameText>
          <SubscribePill type="button" aria-label={COPY.aria.subscribe}>
            {COPY.subscribeLabel}
          </SubscribePill>
        </BrandRow>

        <TitleRow>
          <StoryTitle fallback={COPY.titleFallback} />
          {/* "...more" pinned to the right of the title row, so it stays in
              the same spot regardless of how short / long the title is. Its
              font size inherits so it tracks the title text. */}
          <MoreLink>{COPY.moreLink}</MoreLink>
        </TitleRow>

        <SoundRow>
          <SoundIconWrap>
            <MusicNoteIcon width="1.2em" height="1.2em" fillColor="#ffffff" />
          </SoundIconWrap>
          <SoundText>
            {COPY.soundFallback} · <BrandName />
          </SoundText>
        </SoundRow>

        {/* Comment input affordance. Mirrors YouTube's bottom-of-Shorts
            "Add a comment..." row that paints below the description. Visual
            only — no input handlers — so the chrome reads at parity with
            the real surface without taking keyboard focus. Right-edge
            padding clears the action rail's lowered sound disc, which sits
            in-line with this row. */}
        <CommentBar aria-label={COPY.aria.commentInput}>
          <CommentAvatar>
            <BrandLogo />
          </CommentAvatar>
          <CommentPlaceholder>{COPY.commentPlaceholder}</CommentPlaceholder>
          <CommentEmojiRow aria-hidden="true">
            <CommentEmoji>❤️</CommentEmoji>
            <CommentEmoji>😂</CommentEmoji>
            <CommentEmoji>😢</CommentEmoji>
          </CommentEmojiRow>
        </CommentBar>
      </BottomStrip>

      {/* Always-on thin red scrubber at the very bottom — no thumb dot.
          Pointer-events activate when the playback bridge is ready, so the
          user can drag exactly like the editor's playhead. */}
      <ScrubberRail
        ref={scrubberRef}
        role="slider"
        aria-label={COPY.aria.seek}
        aria-valuemin={0}
        aria-valuemax={Math.max(0, duration)}
        aria-valuenow={Math.max(0, displayTime)}
        onMouseDown={onScrubberMouseDown}
        $interactive={scrubberInteractive}
      >
        <ScrubberFill style={{ width: `${progressPct}%` }} />
      </ScrubberRail>
    </Frame>
  );
};

// ---------------------------------------------------------------------------
// Styles — all dimensions in `em` (1em = 10px @ 360×640 reference).
// ---------------------------------------------------------------------------

const Frame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  font-family:
    'Roboto',
    system-ui,
    -apple-system,
    sans-serif;
  line-height: 1;
  color: #ffffff;
`;

const TopStrip = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4em;
  padding: 1.2em 1.4em 0;
  display: flex;
  align-items: center;
  gap: 0.8em;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

// Back arrow on the far left. Real `<button>` so the affordance reads
// correctly to a screen reader; `pointer-events: auto` opts in so hover
// fires but no `onClick` wire (visual-only — same posture as every other
// overlay action button).
const BackButton = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.4em;
  margin: -0.4em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  flex: 0 0 auto;
`;

// "Shorts" wordmark — bold white text, no play-button glyph. The wrapper
// is non-interactive; we attach an `aria-label` so screen readers
// announce "Shorts" when they reach it.
const ShortsWordmark = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 1.6em;
  font-weight: 700;
  letter-spacing: 0.005em;
  text-shadow: 0 0.05em 0.2em rgba(0, 0, 0, 0.5);
`;

const ShortsLabel = styled.span`
  display: inline-block;
`;

// Pushes the right-side buttons (search + more) to the far right while
// keeping the back arrow + wordmark anchored on the left.
const TopSpacer = styled.div`
  flex: 1 1 auto;
`;

// Shared style for the right-side top-strip buttons (search, more).
const TopActionBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.4em;
  margin: -0.4em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  flex: 0 0 auto;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

// Right-side action rail. Bottom-anchored above the bottom strip's three-
// row layout. `z-index: 1` lifts it above the bottom strip's gradient
// backdrop — the rail and BottomStrip are siblings, but BottomStrip
// renders later in JSX, so without a stacking-context bump its
// `rgba(0,0,0,0.65)` gradient would dim the rail's white icons where they
// overlap the strip.
const RightRail = styled.div`
  position: absolute;
  right: 0.8em;
  /* Lowered so the rail's bottom item (sound disc) sits in-line with the
     bottom comment bar — the disc reads as the right-side affordance of
     the comment row, with the comment input flowing to its left. */
  bottom: 1.4em;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.6em;
`;

const ActionItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4em;
`;

// Thicken every stroked path inside the rail's icons by one unit. Most
// shared icons (LikesIcon, CommentsIcon) render at the default
// `stroke-width: 1`; this CSS override bumps them to 2 so the rail reads
// as confidently as the share / remix glyphs (which already ship at 1.6).
// Assumes the icon paints its outline via a `stroke` (every icon used here
// does). Fill-only glyphs have no `stroke` attribute, so the override
// harmlessly no-ops on them.
const ActionIconBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0.2em;
  margin: -0.2em;
  color: #ffffff;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));

  & svg path,
  & svg circle,
  & svg rect {
    stroke-width: 2;
  }
`;

// Mirrors the icon vertically — used to render the dislike thumb-down by
// flipping `LikesIcon` 180°. Saves us from shipping a near-duplicate SVG.
const FlippedGlyph = styled.span`
  display: inline-flex;
  transform: rotate(180deg);
`;

const ActionCount = styled.span`
  font-size: 1.1em;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
`;

// Sound disc — static channel avatar. Smaller than the bottom-strip avatar
// (2.6em vs 4.2em) so it reads as a rail item, not a duplicate. The dark
// inset ring disambiguates it visually from the brand-row avatar. YouTube
// Shorts does NOT spin the disc on the real surface, so the preview paints
// it stationary as well.
const SoundDiscBtn = styled.button`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  font: inherit;
  filter: drop-shadow(0 0.1em 0.3em rgba(0, 0, 0, 0.5));
`;

// Outer frame — paints the dark frame around the sound-source thumbnail so
// it reads as distinct from the bottom-strip brand avatar. Square (with a
// small corner radius), matching YouTube Shorts' real sound-attribution
// affordance in the right rail.
const SoundDiscRing = styled.span`
  display: inline-flex;
  width: 2.8em;
  height: 2.8em;
  border-radius: 0.4em;
  background: #1a1a1a;
  padding: 0.15em;
  align-items: center;
  justify-content: center;
`;

// Inner square — wraps the brand avatar, clipping it to the same rounded
// square as the frame. Static; no animation.
const SoundDiscInner = styled.span`
  display: inline-block;
  width: 100%;
  height: 100%;
  border-radius: 0.25em;
  overflow: hidden;
`;

// Bottom strip — channel metadata + title + sound row. `bottom` matches
// the scrubber rail's height (0.25em) so the strip's lower edge sits flush
// against the scrubber's top edge.
const BottomStrip = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0.25em;
  padding: 1.4em 1.4em 1.4em;
  display: flex;
  flex-direction: column;
  gap: 0.6em;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.65) 0%,
    rgba(0, 0, 0, 0.35) 60%,
    rgba(0, 0, 0, 0) 100%
  );
  /* Reserve right-edge clearance so the bottom strip never tucks
     underneath the action rail's icons. */
  padding-right: 5.5em;
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7em;
`;

// Avatar — circular brand logo. Sized in `em` so it tracks the rest of
// the chrome under the viewport-derived font scale. Smaller than LinkedIn
// Shorts' 4.2em (which spans two text rows) — the YouTube brand row is a
// single line, so the avatar can be tighter.
const Avatar = styled.div`
  flex: 0 0 auto;
  width: 3em;
  height: 3em;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  box-shadow: 0 0 0 0.1em rgba(255, 255, 255, 0.85) inset;
`;

// `display: block` so `text-overflow: ellipsis` works reliably across
// browsers — spans are inline by default, and ellipsis truncation needs a
// block-formatted box.
const ChannelNameText = styled.span`
  display: block;
  font-size: 1.3em;
  font-weight: 600;
  letter-spacing: 0.005em;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// YouTube Subscribe pill — translucent-white background, white "Subscribe"
// text. Distinct from LinkedIn's outlined Follow pill so the two surfaces
// are recognizable at a glance.
const SubscribePill = styled.button`
  pointer-events: auto;
  flex: 0 0 auto;
  font-size: 1.1em;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 0.4em 0.9em;
  border: none;
  border-radius: 999em;
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
`;

// `position: relative` so the absolutely-positioned MoreLink anchors to
// the title row. `padding-right` reserves a gutter wide enough for
// "...more" at the same font size as the title (scales with chrome).
const TitleRow = styled.div`
  position: relative;
  font-size: 1.2em;
  font-weight: 500;
  line-height: 1.3;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  padding-right: 4em;
  /* Single-line title with truncation. */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

// Stationary at the right edge of the title row. Inherits the title's
// font-size so "...more" reads at the exact same size as the title text
// to its left.
const MoreLink = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  font-size: inherit;
  line-height: 1.3;
  opacity: 0.8;
`;

// Sound row — music-note glyph + one-line audio attribution. Slightly
// dimmer than the title row (`opacity: 0.85`) to mirror YouTube's actual
// hierarchy.
const SoundRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 1.05em;
  font-weight: 400;
  opacity: 0.85;
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  /* Single-line truncation for long audio names. */
  min-width: 0;
`;

const SoundIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
`;

const SoundText = styled.span`
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Captions sized like LinkedIn Shorts — small, centered. Anchored to clear
// YouTube's four-row bottom strip (brand / title / sound / comment input).
// Caption-to-brand-row gap stays comparable.
const CaptionAnchor = styled.div<{ $visible: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 16.5em;
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding: 0;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 200ms ease-out;
`;

// Caption box width is em-based (not a `%` of the rect) so it tracks the
// chrome's scale rather than the rect's literal width. At reference
// (1em = 10px, rect.width = 360) `18em` ≈ 180px ≈ 50% of rect width — and
// stays at that proportion under every clamp/aspect drift, since `fontPx`
// itself is derived from `min(width, height)`.
const CaptionBox = styled.div`
  display: inline-block;
  color: #ffffff;
  font-size: 1.3em;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.35;
  text-align: center;
  max-width: 18em;
`;

const CaptionLines = styled.span`
  display: inline;
  background: rgba(0, 0, 0, 0.92);
  padding: 0.1em 0.4em;
  border-radius: 0.2em;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
`;

// Thin always-on red scrubber line — no thumb. YouTube's brand-red fill
// over an opaque dark gray track (the unfilled portion is opaque rather
// than translucent so the playhead reads cleanly against any frame).
const ScrubberRail = styled.div<{ $interactive: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 0.25em;
  background: #5a5a5a;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
`;

const ScrubberFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ff0000;
`;

// Comment input row — small avatar + placeholder text + reaction emojis.
// Visual only; no real input. Sized so it tracks the rest of the bottom
// strip under the viewport-derived font scale. Right gutter widens here
// (instead of being inherited from BottomStrip) so the emoji cluster
// doesn't crowd the sound disc that now sits in-line on the right.
const CommentBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6em;
  margin-top: 0.2em;
  padding: 0.5em 0.8em;
  border-radius: 999em;
  background: rgba(255, 255, 255, 0.14);
  font-size: 1.1em;
  font-weight: 400;
`;

// Smaller than the brand-row Avatar (2em vs 3em) — the comment row reads
// as a secondary affordance, not a second brand row.
const CommentAvatar = styled.div`
  flex: 0 0 auto;
  width: 2em;
  height: 2em;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.18);
`;

// Placeholder text. `flex: 1 1 auto` lets it absorb the row's remaining
// width so the emoji cluster pins to the right edge regardless of font
// scale.
const CommentPlaceholder = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  color: rgba(255, 255, 255, 0.78);
  text-shadow: 0 0.1em 0.3em rgba(0, 0, 0, 0.5);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CommentEmojiRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  flex: 0 0 auto;
`;

// Slightly larger than the placeholder text so the emoji cluster reads as
// a row of reaction taps rather than another text run.
const CommentEmoji = styled.span`
  font-size: 1.2em;
  line-height: 1;
`;
